/**
 * /operations/excuses/quota-watch — Session 3a PR 16.
 *
 * Employees approaching or breaching excuse quota limits.
 * Mutaba'aa-owned per Session 3a §4.15 (quota-floor compliance —
 * excuse policies have monthly/yearly caps per type).
 *
 * AI surfaces (commandment-3 invariant):
 *   1. AiBriefPanel right rail (Mutaba'aa narration)
 *   2. InlineAISuggestion cards (auto-veto · type policy review)
 *   3. TraceableValue wrapping monthly_utilization + yearly_utilization
 *      + used_this_month (≥3 wrappers)
 */

import { useQuery } from "@tanstack/react-query";
import { ListChecks, Scan, ShieldX } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  InlineAISuggestion,
  PageHeader,
  TraceableValue,
} from "../components/ui";
import {
  session3aApi,
  type QuotaWatchItem,
} from "../lib/session3a-api";
import { useLegalEntityGate } from "../lib/legalEntity";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { severityChip, showAlertTrace } from "./_session3aHelpers";

type Filter = "all" | "critical" | "exceeded";

function statusTone(s: QuotaWatchItem["status"]): "neutral" | "info" | "attention" | "critical" | "veto" {
  if (s === "exceeded") return "veto";
  if (s === "critical") return "critical";
  if (s === "attention") return "attention";
  return "info";
}

export function ExcuseQuotaWatchPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [filter, setFilter] = useState<Filter>("all");
  const [thresholdPct, setThresholdPct] = useState<number>(80);

  const list = useQuery({
    queryKey: ["excuse-quota-watch", legalEntityId, thresholdPct],
    queryFn: () =>
      session3aApi.listQuotaWatch(legalEntityId ?? "", thresholdPct),
    enabled: !!legalEntityId,
  });

  const items: QuotaWatchItem[] = list.data?.items ?? [];

  const stats = useMemo(() => {
    const critical = items.filter((r) => r.status === "critical");
    const exceeded = items.filter((r) => r.status === "exceeded");
    const types: Record<string, number> = {};
    items.forEach((r) => {
      types[r.excuse_type_code] = (types[r.excuse_type_code] ?? 0) + 1;
    });
    const top = Object.entries(types).sort((a, b) => b[1] - a[1])[0];
    return {
      total: items.length,
      critical: critical.length,
      exceeded: exceeded.length,
      topType: top ? { code: top[0], count: top[1] } : null,
    };
  }, [items]);

  const filtered = useMemo(() => {
    if (filter === "critical") return items.filter((r) => r.status === "critical");
    if (filter === "exceeded") return items.filter((r) => r.status === "exceeded");
    return items;
  }, [items, filter]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("operations.excuses_quota.title", "Quota Watch")}
          subtitle={t(
            "operations.excuses_quota.subtitle",
            "Employees approaching or breaching excuse-type caps. Mutaba'aa enforces the monthly/yearly limits; auto-veto activates at over-quota.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => setFilter("all")}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                filter === "all"
                  ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("operations.excuses_quota.filter.all", "All")}
            </button>
            <button
              type="button"
              onClick={() => setFilter(filter === "critical" ? "all" : "critical")}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                filter === "critical"
                  ? "border-rose-300 bg-rose-50 text-rose-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("operations.excuses_quota.filter.critical", "Critical (80%+)")}
            </button>
            <button
              type="button"
              onClick={() => setFilter(filter === "exceeded" ? "all" : "exceeded")}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                filter === "exceeded"
                  ? "border-purple-300 bg-purple-50 text-purple-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("operations.excuses_quota.filter.exceeded", "Exceeded")}
            </button>
            <label className="ms-2 flex items-center gap-2 text-xs text-text-secondary">
              {t("operations.excuses_quota.threshold", "Threshold %:")}
              <input
                type="number"
                value={thresholdPct}
                min={0}
                max={100}
                onChange={(e) => setThresholdPct(Number(e.target.value) || 0)}
                className="w-16 rounded-lg border border-border-subtle px-2 py-1"
              />
            </label>
          </div>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "operations.excuses_quota.scan_note",
                  "Manual trigger runs the quota scanner immediately. APScheduler also runs daily; results populate this view.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Scan className="h-4 w-4" />
            {t("operations.excuses_quota.cta.scan", "Run quota audit now")}
          </button>
        </div>

        {stats.exceeded > 0 && (
          <InlineAISuggestion
            agent="mutabaa"
            icon={<ShieldX className="h-4 w-4" />}
            severity="critical"
            headline={t(
              "operations.excuses_quota.suggestion.veto_headline",
              `Mutaba'aa veto: ${stats.exceeded} employees over monthly cap — new excuse requests auto-rejected.`,
            )}
            body={t(
              "operations.excuses_quota.suggestion.veto_body",
              "Auto-rejection writes a refused workflow_event. Employees can appeal via Khidmat or HR review.",
            )}
            primaryAction={{
              label: t(
                "operations.excuses_quota.suggestion.see_exceeded",
                "See exceeded",
              ),
              onClick: () => setFilter("exceeded"),
            }}
            suggestionKey={`quota-watch-exceeded-${stats.exceeded}`}
          />
        )}
        {stats.topType && stats.topType.count >= 5 && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<ListChecks className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "operations.excuses_quota.suggestion.type_headline",
              `Pattern: ${stats.topType.code} is hitting cap for ${stats.topType.count} employees.`,
            )}
            body={t(
              "operations.excuses_quota.suggestion.type_body",
              "Tashghil suggests reviewing the policy threshold — the type may be under-resourced.",
            )}
            primaryAction={{
              label: t(
                "operations.excuses_quota.suggestion.open_policy",
                "Open excuse policy",
              ),
              onClick: () => window.location.assign("/policies/excuse-types"),
            }}
            suggestionKey={`quota-watch-type-${stats.topType.code}`}
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("operations.excuses_quota.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("operations.excuses_quota.col.type", "Type")}</th>
                  <th className="px-3 py-2">{t("operations.excuses_quota.col.monthly", "This month")}</th>
                  <th className="px-3 py-2">{t("operations.excuses_quota.col.monthly_util", "Monthly util")}</th>
                  <th className="px-3 py-2">{t("operations.excuses_quota.col.yearly", "This year")}</th>
                  <th className="px-3 py-2">{t("operations.excuses_quota.col.yearly_util", "Yearly util")}</th>
                  <th className="px-3 py-2">{t("operations.excuses_quota.col.status", "Status")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => {
                  const tone = statusTone(row.status);
                  return (
                    <tr
                      key={`${row.employee_id}-${row.excuse_type_code}`}
                      className="border-t border-border-subtle hover:bg-surface-sunken/40"
                    >
                      <td className="px-3 py-2">
                        <Link
                          to={`/people/employees/${row.employee_id}`}
                          className="font-medium text-[#2260C9] hover:underline"
                        >
                          {row.employee_name_en}
                        </Link>
                        <div className="text-xs text-text-tertiary">{row.employee_code}</div>
                      </td>
                      <td className="px-3 py-2 text-text-secondary">{row.excuse_type_label_en}</td>
                      <td className="px-3 py-2">
                        <TraceableValue
                          onTraceClick={() =>
                            showAlertTrace(
                              t(
                                "operations.excuses_quota.trace.monthly",
                                `used_this_month = count of approved excuses in current month. Denied + cancelled excuses do NOT count.`,
                              ),
                            )
                          }
                        >
                          <span className="text-xs text-text-secondary">
                            {row.used_this_month} / {row.monthly_limit}
                          </span>
                        </TraceableValue>
                      </td>
                      <td className="px-3 py-2">
                        <TraceableValue
                          onTraceClick={() =>
                            showAlertTrace(
                              t(
                                "operations.excuses_quota.trace.monthly_util",
                                `monthly_utilization = used_this_month / monthly_limit. ${row.monthly_utilization_pct.toFixed(0)}%.`,
                              ),
                            )
                          }
                        >
                          <div className="flex items-center gap-1.5">
                            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-200">
                              <div
                                className={`h-full ${
                                  tone === "veto"
                                    ? "bg-purple-600"
                                    : tone === "critical"
                                      ? "bg-rose-600"
                                      : tone === "attention"
                                        ? "bg-amber-500"
                                        : "bg-brand-blue"
                                }`}
                                style={{ width: `${Math.min(100, row.monthly_utilization_pct)}%` }}
                              />
                            </div>
                            <span className="text-xs text-text-secondary">
                              {row.monthly_utilization_pct.toFixed(0)}%
                            </span>
                          </div>
                        </TraceableValue>
                      </td>
                      <td className="px-3 py-2 text-xs text-text-secondary">
                        {row.used_this_year} / {row.yearly_limit}
                      </td>
                      <td className="px-3 py-2">
                        <TraceableValue
                          onTraceClick={() =>
                            showAlertTrace(
                              t(
                                "operations.excuses_quota.trace.yearly_util",
                                `yearly_utilization = used_this_year / yearly_limit. Yearly cap acts as a hard ceiling regardless of monthly resets.`,
                              ),
                            )
                          }
                        >
                          <span className="text-xs text-text-secondary">
                            {row.yearly_utilization_pct.toFixed(0)}%
                          </span>
                        </TraceableValue>
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip(tone)}`}
                        >
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!list.isLoading && filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {t(
              "operations.excuses_quota.empty",
              "No employees near excuse-quota thresholds. ✦ Mutaba'aa: healthy.",
            )}
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="mutabaa"
          liveContext={{
            total: stats.total,
            attention: stats.critical,
            critical: stats.exceeded,
            top_kind: t("operations.excuses_quota.title", "Quota Watch"),
            window_label: "live",
          }}
        />
      </aside>
    </div>
  );
}
